import { css } from 'aphrodite/no-important';
import React, { FC, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { isExhibitorUser } from 'redux/reducers/user';
import { RootState } from 'redux/store';
import { GUEST_USER_REGEX, ROUTE } from 'util/constants';
import { makeSlug } from 'util/helpers';
import { IQuickLink } from '../../QuickLinks/QuickLinks';
import { Branding, BrandingType } from '../Branding/Branding';
import { ILink, ISubLink, MobileNavLink } from './MobileNavLink/MobileNavLink';
import { style } from './style';

export interface MobileNavProps {
  toggleMenu: (isOpen: boolean) => void;
  onLogout: () => void;
}

export const MobileNav: FC<MobileNavProps> = ({ toggleMenu, onLogout }) => {
  const cmsText = useSelector((state: RootState) => state.widget.cms.text);
  const isExhibitor = useSelector(isExhibitorUser);
  const { header, floating_quick_links } = cmsText;
  const { logo_url, header_text, navigation, welcome } = header;

  const userData = useSelector((state: RootState) => state.user.data);
  const [isMenuOpen, setMenuOpen] = useState(true);
  const [openDropdown, setOpenDropdown] = useState<string>('');
  const scrollPosition = useRef<number>();
  const openAnimationDurationMs = 250;

  const styles = style({ isVerticalBranding: false, isMenuOpen, openAnimationDurationMs });

  /** Layout Manipulation */

  useLayoutEffect(() => {
    const $body = document.body;
    if (isMenuOpen) {
      // Save the user's current scroll position so that when the modal is closed the user will be at the same position
      scrollPosition.current = window.pageYOffset;
      $body.style.overflow = 'hidden';
      $body.style.position = 'fixed';
      $body.style.width = '100%';
      $body.style.top = `-${scrollPosition.current}px`;
    }
  }, [isMenuOpen]);

  const unlockScrolling = () => {
    // Unlock body scrolling and restore the scroll position
    const $body = document.body;
    $body.style.removeProperty('overflow');
    $body.style.removeProperty('position');
    $body.style.removeProperty('top');
    $body.style.removeProperty('width');
    window.scrollTo(0, scrollPosition.current || 0);
  };

  /** Handlers */

  const closeMenu = () => {
    setMenuOpen(false);

    setTimeout(() => {
      toggleMenu(false);
      unlockScrolling();
    }, openAnimationDurationMs);
  };

  const handleLogout = () => {
    unlockScrolling();
    onLogout();
  };

  const handleDropdownToggle = (link: any): void => {
    const linkId: string = makeSlug(link.text);
    if (openDropdown && openDropdown === linkId) return setOpenDropdown('');
    setOpenDropdown(linkId);
  };

  /** Utils */

  const isDropdownOpenById = (link: any): boolean => {
    const linkId: string = makeSlug(link.text);
    return !!openDropdown && openDropdown === linkId;
  };

  const hasDropdown = (route: string) => {
    switch (route) {
      case '/partner-pavilion':
      case '/innovation-zone':
      case '/technical-tracks':
        return true;
      default:
        return false;
    }
  };

  const getDropdownOptionsByRoute = (route: string): string[] => {
    switch (route) {
      case '/partner-pavilion':
        return (header.partner_categories as string[]) || [];
      case '/innovation-zone':
        return (header.innovation_zone_categories as string[]) || [];
      case '/technical-tracks':
        return (header.technical_tracks_categories as string[]) || [];
      default:
        return [];
    }
  };

  const getDropdownGenericSubLinkByRoute = (route: string): ISubLink | null => {
    switch (route) {
      case '/partner-pavilion':
        return { text: 'All Partners', route };
      case '/innovation-zone':
        return { text: 'All Platforms', route };
      default:
        return null;
    }
  };

  const getWelcomeString = () => {
    const maxChar = 15;
    let userName: string = userData?.user_first_name || '';
    const isGuestUser = GUEST_USER_REGEX.test(userName);
    if (isGuestUser) return welcome.guest_text;

    if (userName.length > maxChar) {
      userName = userName.substr(0, maxChar) + '&hellip;';
    }

    return welcome.text.replace('{user_name}', `${userName}`);
  };

  /** React DOM Elements */

  const getHamburgerButton = (open: boolean): JSX.Element => {
    //Note: Only displayed when viewport < mobile
    return (
      <div className={css(styles.hamburger, open && styles.hamburgerActive)} onClick={() => closeMenu()}>
        <span /> <span /> <span />
      </div>
    );
  };

  const getWelcomeDropdown = (): JSX.Element => {
    const link: ILink = {
      text: getWelcomeString(),
      sub_text: '',
      route: ROUTE.ACHIEVEMENTS,
    };

    const sublinks: ISubLink[] = [
      {
        text: 'My Achievements',
        route: ROUTE.ACHIEVEMENTS,
        icon: '/images/achievement-medal.png',
      },
    ];

    return (
      <MobileNavLink
        link={link}
        sublinks={sublinks}
        isDropdownOpen={isDropdownOpenById(link)}
        onDropdownToggle={() => handleDropdownToggle(link)}
        onClick={() => closeMenu()}
      />
    );
  };

  const getMobileNavLinks = () => {
    return navigation
      .filter((link: any) => {
        return !isExhibitor || link.route !== ROUTE.SURVEY;
      })
      .map((link: any, index: number) => {
        if (!hasDropdown(link.route))
          return <MobileNavLink link={link} key={index.toString()} onClick={() => closeMenu()} />;
        const options = getDropdownOptionsByRoute(link.route);
        return getNavDropdown(index, link, options);
      });
  };

  const getNavDropdown = (id: number, link: ILink, options: string[]): JSX.Element => {
    const sublinks: ISubLink[] = options.map((option) => {
      return {
        text: option,
        route: `${link.route}/${makeSlug(option)}`,
      };
    });
    const genericSublink = getDropdownGenericSubLinkByRoute(link.route);
    if (genericSublink) sublinks.unshift(genericSublink);

    return (
      <MobileNavLink
        key={id.toString()}
        link={link}
        sublinks={sublinks}
        isDropdownOpen={isDropdownOpenById(link)}
        onDropdownToggle={() => handleDropdownToggle(link)}
        onClick={() => closeMenu()}
      />
    );
  };

  const getQuickLinks = (): JSX.Element[] => {
    const quickLinks: ILink[] = floating_quick_links
      .filter((link: any) => {
        return isExhibitor && link.url === ROUTE.SURVEY ? false : true;
      })
      .map((link: IQuickLink) => {
        return { text: link.text, route: link.url, icon: link.icon };
      });

    return quickLinks.map((link: ILink, index: number) => {
      return <MobileNavLink key={index.toString()} link={link} onClick={() => closeMenu()} />;
    });
  };

  const getLogout = (): JSX.Element => {
    return <MobileNavLink link={{ text: 'Log Out', route: '' }} onClick={handleLogout} />;
  };

  return (
    <div className={css(styles.navOverlay)}>
      <div className={css(styles.navContainer)}>
        <div className={css(styles.navHeader)}>
          <Branding type={BrandingType.HORIZONTAL} logoUrl={logo_url} isVisible={true} copy={header_text} />
          {getHamburgerButton(isMenuOpen)}
        </div>

        {getWelcomeDropdown()}
        {getMobileNavLinks()}
        {getQuickLinks()}
        {userData && getLogout()}
      </div>
    </div>
  );
};
